import {
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';

import {
    add,
    deleteRecord,
    fetch as fetchOne,
    fetchRecords,
    update,
} from '../services/RegistrarsService';

import {Registrar} from '../types';

// FNP-943: records are React Query's job. Selection, paging and keyword stay in
// Redux. The service layer is unchanged — these hooks call the same
// RegistrarsService functions the Redux thunks called.

export const registrarsKeys = {
    all: ['registrars'] as const,
    lists: () => [...registrarsKeys.all, 'list'] as const,
    list: (filter: object) => [...registrarsKeys.lists(), filter] as const,
    details: () => [...registrarsKeys.all, 'detail'] as const,
    detail: (id: string) => [...registrarsKeys.details(), id] as const,
};

export const useRegistrarsQuery = (filter: object = {}) => {
    return useQuery({
        queryKey: registrarsKeys.list(filter),
        queryFn: async (): Promise<Registrar[]> => {
            const records = await fetchRecords(filter);
            return records ?? [];
        },
    });
};

export const useRegistrarQuery = (registrarId?: string) => {
    return useQuery({
        queryKey: registrarsKeys.detail(registrarId ?? ''),
        queryFn: async () => {
            if (typeof registrarId === 'undefined') {
                throw new Error('registrarId is required parameter');
            }
            return await fetchOne(registrarId);
        },
        enabled: typeof registrarId !== 'undefined' && registrarId !== '',
    });
};

export const useCreateRegistrarMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (values: Registrar) => add(values),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: registrarsKeys.lists()});
        },
    });
};

export const useUpdateRegistrarMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
                         id,
                         record
                     }: { id: string, record: Registrar }) => update(id, record),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({queryKey: registrarsKeys.lists()});
            queryClient.invalidateQueries({queryKey: registrarsKeys.detail(variables.id)});
        },
    });
};

export const useDeleteRegistrarMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteRecord(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: registrarsKeys.lists()});
        },
    });
};
